import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MakeSkeleton from './MakeSkeleton';
import ErrorState from '../ErrorState/ErrorState';
import { useCachedFetch } from './../../hooks/useCachedFetch';
import { AnimatedOnScroll } from '../AnimatedOnScroll';
import './Make.scss';

const Make = ({ pageKey }) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;

  const {
    data: makeData,
    loading: dataLoading,
    error: dataError,
    refetch
  } = useCachedFetch('/data/locales/make.json');

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (dataLoading) return;

    if (dataError) {
      setHasError(true);
      setIsLoading(false);
      return;
    }

    const data = makeData?.MakeData?.[pageKey]?.[currentLang] || makeData?.MakeData?.[pageKey]?.en;

    if (data && data.title && data.subtitle) {
      setHasError(false);
    } else {
      setHasError(true);
    }

    setIsLoading(false);
  }, [dataLoading, dataError, makeData, pageKey, currentLang]);

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  const pageData = useMemo(() => {
    return makeData?.MakeData?.[pageKey]?.[currentLang] || makeData?.MakeData?.[pageKey]?.en || null;
  }, [makeData, pageKey, currentLang]);

  const { title, subtitle, buttonText, buttonLink } = useMemo(() => {
    if (!pageData) {
      return { title: '', subtitle: '', buttonText: '', buttonLink: '/' };
    }

    return {
      title: pageData.title || '',
      subtitle: pageData.subtitle || '',
      buttonText: pageData.buttonText || '',
      buttonLink: pageData.buttonLink || '/'
    };
  }, [pageData]);

  const gradientSvg = useMemo(() => (
    <svg
      className="looking-for-bg"
      width="100%"
      height="100%"
      viewBox="0 0 400 400"
    >
      <defs>
        <linearGradient id="grad1" gradientTransform="rotate(-45)">
          <stop offset="0%" stopColor="#0095ff" />
          <stop offset="45%" stopColor="#3c59e6" />
          <stop offset="80%" stopColor="#021c8b" />
        </linearGradient>
      </defs>
      <polygon points="0,0 800,0 700,600 0,600" fill="url(#grad1)" />
    </svg>
  ), []);

  if (isLoading) {
    return <MakeSkeleton />;
  }

  if (hasError || !pageData) {
    return <ErrorState onRetry={handleRetry} />;
  }

  return (
    <div className='home__make__section'>
      <div className='left__side__make__section'>
        {gradientSvg}
      </div>
      <div className='right__side__make__section'>

        <AnimatedOnScroll animation="fade-left" delay={100}>
          <h2 className='make__title'>{title}</h2>
          <h3 className='make__subtitle'>{subtitle}</h3>

          {buttonLink.startsWith('http') || buttonLink.startsWith('mailto:') ? (
            <a
              href={buttonLink}
              target="_blank"
              rel="noopener noreferrer"
              className='make__button'
            >
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              {buttonText}
            </a>
          ) : (
            <Link to={buttonLink} className='make__button'>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              {buttonText}
            </Link>
          )}
        </AnimatedOnScroll>
      </div>
    </div>
  );
};

export default React.memo(Make);